"use client";

// Client-side utils and components

// Blocks
export {
  AddressSelector,
  type AddressSelectorData,
} from "./blocks/address-selector/address-selector";
// Cart Header
export {
  CartHeader,
  type CartHeaderProps,
} from "./blocks/cart-header/cart-header";
// Cart - order summary, product box, and list title
export {
  CartOrderSummary,
  type CartOrderSummaryOrderPurpose,
  type CartOrderSummaryPrices,
  type CartOrderSummaryProps,
} from "./blocks/cart-order-summary/cart-order-summary";
export {
  CartProductBox,
  type CartProductBoxProps,
  type ProductVariant,
  type UserType,
} from "./blocks/cart-product-box/cart-product-box";
export {
  CartProductListTitle,
  type CartProductListTitleProps,
} from "./blocks/cart-product-list-title/cart-product-list-title";
export {
  type AddressData,
  type DeliveryOption,
  DeliveryPaymentInfo,
  type DeliveryPaymentInfoProps,
  type PaymentMethod,
} from "./blocks/delivery-payment-info/delivery-payment-info";
export {
  type DivisionItem,
  Divisions,
  type DivisionsInspectorProps,
  type DivisionsProps,
} from "./blocks/divisions/divisions";
export { DivisionsPreview } from "./blocks/divisions/divisions.preview";
export {
  DocumentListCard,
  type DocumentListCardProps,
} from "./blocks/document-list-card/document-list-card";
export { GenericModal } from "./blocks/generic-modal/generic-modal";
export {
  HeroBanner,
  type HeroBannerProps,
} from "./blocks/hero-banner/hero-banner";
export {
  HeroSection,
  type HeroSectionProps,
} from "./blocks/hero-section/hero-section";
export {
  type HeroSectionSlide,
  HeroSectionSlider,
  type HeroSectionSliderMedia,
  type HeroSectionSliderProps,
} from "./blocks/hero-section/hero-section-slider";
export { HERO_SECTION_SLIDER_MAX_SLIDES } from "./blocks/hero-section/hero-section-slider.constants";
export {
  ImageArrangement,
  type ImageArrangementItem,
  type ImageArrangementProps,
} from "./blocks/image-arrangement/image-arrangement";
export {
  ImageGallery,
  type ImageGalleryProps,
} from "./blocks/image-gallery/image-gallery";
export type {
  GalleryDevice,
  GalleryImageData,
  GalleryMicrocopies,
  GalleryMouseState,
} from "./blocks/image-gallery/image-gallery.types";
export {
  NewsTicker,
  type NewsTickerProps,
} from "./blocks/news-ticker/news-ticker";
export {
  PoiImageHotspot,
  type PoiImageHotspotData,
  type PoiImageHotspotDetailsActions,
  type PoiImageHotspotDetailsState,
  type PoiImageHotspotItem,
  type PoiImageHotspotProps,
} from "./blocks/poi-image-hotspot/poi-image-hotspot";
export {
  ProductBox,
  type ProductBoxProps,
  type ProductBoxViewItem,
} from "./blocks/product-box/product-box";
export {
  ProductCardB2B,
  type ProductCardB2BBadge,
  type ProductCardB2BPrice,
  type ProductCardB2BProps,
} from "./blocks/product-card-b2b/product-card-b2b";
export {
  ProductCardB2BClient,
  type ProductCardB2BClientProps,
} from "./blocks/product-card-b2b/product-card-b2b.client";
export {
  type ProductHighlight,
  ProductHighlights,
  type ProductHighlightsProps,
} from "./blocks/product-highlights/product-highlights";
export { ProductMediaGallery } from "./blocks/product-media-gallery/product-media-gallery";
export type {
  ProductMediaGalleryMicrocopies,
  ProductMediaGalleryProps,
} from "./blocks/product-media-gallery/product-media-gallery.types";
export {
  Slider,
  type SliderProps,
  type SliderSlide,
  type SliderSlideMedia,
} from "./blocks/slider/slider";
export {
  type TeaserItem,
  TeaserSection,
  type TeaserSectionInspectorProps,
  type TeaserSectionProps,
} from "./blocks/teaser-section/teaser-section";
// Accordion component
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  type AccordionItemData,
  AccordionPrimitive,
  type AccordionProps,
  AccordionTrigger,
} from "./elements/accordion/accordion";
export { Alert } from "./elements/alert/alert";
// Elements - AlertDialog
export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./elements/alert-dialog/alert-dialog";
// Elements - Button
export {
  Button,
  type ButtonProps,
  buttonVariants,
  ChangingButton,
  type ChangingButtonAction,
  type ChangingButtonProps,
  type LinkProps,
  LinkProvider,
  useLink,
} from "./elements/button/button";
// Elements - Checkbox and radio input
export {
  ChoiceInput,
  type ChoiceInputProps,
} from "./elements/choice-input/choice-input";
export {
  type ColorSwatchGroupWithImagesProps,
  type ColorSwatchWithImage,
  FALLBACK_COLOR,
  resolveSwatchHexColors,
} from "./elements/color-swatch/color-swatch.utils";
// Image component and provider
export { Image, type ImageProps } from "./elements/image/image";
export { ImageProvider } from "./elements/image/image-provider";
export { useImage } from "./elements/image/use-image";
// Elements - Input
export { Input, type InputProps } from "./elements/input/input";
// Elements - Label
export { Label, type LabelProps } from "./elements/label/label";
// Elements - Pagination
export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./elements/pagination/pagination";
// Elements - Product Variant Selector
export {
  ProductVariantSelector,
  type ProductVariantSelectorOption,
  type ProductVariantSelectorProps,
} from "./elements/product-variant-selector/product-variant-selector";
// Elements - Progress
export { Progress, type ProgressProps } from "./elements/progress/progress";
// Elements - Searchable Select
export {
  SearchableSelect,
  type SearchableSelectOption,
  type SearchableSelectProps,
} from "./elements/searchable-select/searchable-select";
// Elements - Select
export {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./elements/select/select";
// Elements - Switch
export { Switch } from "./elements/switch/switch";
// Elements - Text
export { Text, type TextProps } from "./elements/text/text";
// Elements - Video
export {
  useVlitePlaybackState,
  useVlitePlayer,
  type VliteEventHandlers,
  type VliteInstance,
  type VliteOptions,
  type VlitePlaybackState,
  VlitePlayer,
  type VlitePlayerProps,
  type VlitePlayerType,
  type VlitePlugin,
  type VlitePluginRegistration,
  type VliteProviderRegistration,
} from "./elements/video";
// Hooks
export { useDebouncedCallback } from "./hooks/use-debounced-callback";
// Layout
export * from "./layout/grid";
// Date Picker components
export { Calendar, CalendarDayButton } from "./patterns/calendar/calendar";
export {
  CardDocument,
  type CardDocumentProps,
} from "./patterns/card-document/card-document";
// Card components
export {
  CardGeneric,
  type CardGenericProps,
} from "./patterns/card-generic/card-generic";
export {
  CardProduct,
  type CardProductProps,
} from "./patterns/card-product/card-product";
// Carousel component - moved to dedicated subpath to avoid dependency leakage
// NOTE: Import from the dedicated subpath: '@scott/ui/patterns/carousel'
export { DatePicker } from "./patterns/date-picker/date-picker";
export { DatePlaceholder } from "./patterns/date-placeholder/date-placeholder";
export {
  FormattedDate,
  type FormattedDateProps,
} from "./patterns/formatted-date/formatted-date";
export {
  LineSteps,
  type LineStepsProps,
} from "./patterns/line-steps/line-steps";
export {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./patterns/popover/popover";
export {
  ProductFilters,
  type ProductFiltersProps,
} from "./patterns/product-filters/product-filters";
export {
  type AvailabilityLocation,
  type AvailabilityStatus,
  ProductInputQuantity,
  type ProductInputQuantityProps,
} from "./patterns/product-input-quantity/product-input-quantity";
// Tabs component
export { Tabs, TabsContent, TabsList, TabsTrigger } from "./patterns/tabs/tabs";
export {
  type UserAddressData,
  UserAddressModal,
  type UserAddressModalProps,
} from "./patterns/user-address-modal/user-address-modal";
export type { UserAddressFormData } from "./patterns/user-address-modal/user-address-modal.schema";
export {
  UserCardAddress,
  type UserCardAddressProps,
} from "./patterns/user-card-address/user-card-address";
export {
  UserCardPreferences,
  type UserCardPreferencesProps,
} from "./patterns/user-card-prefrences/user-card-prefrences";
export {
  UserCardProfile,
  type UserCardProfileProps,
  type UserRole,
} from "./patterns/user-card-profile/user-card-profile";
export {
  UserCardSecurity,
  type UserCardSecurityProps,
} from "./patterns/user-card-security/user-card-security";
export {
  UserFormModal,
  type UserFormModalProps,
} from "./patterns/user-form-modal/user-form-modal";
export type { UserFormModalData } from "./patterns/user-form-modal/user-form-modal.schema";
export {
  type UserAccount,
  UserMyCompany,
  type UserMyCompanyProps,
} from "./patterns/user-my-company/user-my-company";

// Universal utils (can be used on client and server)
export * from "./utils/cn";
// Utils that require client-side functionality
export * from "./utils/use-action-state-without-reset";
export * from "./utils/word-count";
